/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useCallback, useRef } from 'react';
import { pickBy, values } from 'lodash';
import { useStore } from '@tmsw/powerups.store';
import { useTranslation } from '@tmsw/powerups.intl.hooks.use-translation';
import { PageConfigContext } from './context';

/**
 * @typedef {PagesMap}
 */
export type PagesMap = { [key: string]: number };
export type PageComponents = { [key: string]: any };
export type PageInput = string | number;
export type PageConfigProviderProps = {
  pagesMap?: PagesMap;
  pageComponents?: PageComponents; // array of page components (optional for now)
  children?: ReactNode;
};
export type GetPageKey = (pageId: number) => string | null;
export type GetPageId = (pageKey: string) => number | null;
export type HandlePageInput = (pageKeyOrId: PageInput) => number | null;
export type GoToPage = (targetPage: PageInput) => void;
export type GoToPrevPage = () => void;
export type GoToNextPage = () => void;

/**
 * @param {any} pageId:number
 * @param {any} pagesMap:PagesMap
 * @returns {string|null}
 */
function getMappedPageKey(pageId: number, pagesMap: PagesMap): string | null {
  const entry = Object.entries(pagesMap)
    .map(([key, id]) => ({ key, id }))
    .find((record) => pageId === record.id);
  return entry ? entry.key : null;
}

/**
 * @param {string} pageKey
 * @param {PagesMap} pagesMap
 * @returns {number|null}
 */
function getMappedPageId(pageKey: string, pagesMap: PagesMap) {
  return pagesMap[pageKey] ?? null;
}

/**
 * @param <PageConfigProviderProps> props
 * @returns {Element}
 */
export function PageConfigProvider({
  pagesMap,
  pageComponents,
  children,
}: PageConfigProviderProps) {
  const { __t } = useTranslation();
  pagesMap = Object.assign(pagesMap ?? {}, __t('PAGES_MAP'));
  const idxs = Object.values(pagesMap);
  const kys = Object.keys(pagesMap);
  const fns = Object.values(
    pageComponents ??
      Array(idxs.length).fill(() => {
        return null;
      })
  );
  const pagesMapWithComponents = kys.map((key: any, idx: number) => {
    // console.log('key, idx', key, idx);
    return { key, idx, orgIdx: idxs[idx], fn: fns[idx] };
  });
  pageComponents = Object.fromEntries(
    pagesMapWithComponents.map((obj) => [obj.key, obj.fn])
  );
  // console.log({keys, fns, idxs, pagesMapWithComponents});
  const currentPage = useStore((state) => state.currentPage);
  const setPage = useStore(useCallback((state) => state.setPage, []));

  /**
   * Get the element key at this index
   */
  const getPageKey: GetPageKey = useCallback(
    (pageId) => getMappedPageKey(pageId, pagesMap),
    [pagesMap]
  );

  /**
   * Get the element index for this key
   */
  const getPageId: GetPageId = useCallback(
    (pageKey) => getMappedPageId(pageKey, pagesMap),
    [pagesMap]
  );

  /**
   * Sets the current page in state;
   * Note: GameWrapper is where the magic happens
   */
  const goToPage: GoToPage = useCallback((nextPage) => {
    /**
     * Utility function to handle goToPage functions that accept either the the page index or key
     * @param {number|string} pageKeyOrId
     * @returns {number|null}
     */
    const handlePageInput: HandlePageInput = (pageKeyOrId) =>
      typeof pageKeyOrId === 'string' ? getPageId(pageKeyOrId) : pageKeyOrId;
    setPage(handlePageInput(nextPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToNextPage: GoToNextPage = useCallback(
    () => goToPage(currentPage + 1),
    [currentPage]
  );
  const goToPrevPage: GoToPrevPage = useCallback(
    () => goToPage(currentPage - 1),
    [currentPage]
  );

  const filterPageComponents = (pc) => {
    const pageKeys = Object.keys(pagesMap);
    return values(
      pickBy(pc, (...args) => {
        // console.log('args', args);
        return pageKeys.indexOf(args[1]) !== -1;
      })
    );
  };

  const pageComponentsRef = useRef(filterPageComponents(pageComponents));

  return (
    <PageConfigContext.Provider
      value={{
        currentPage,
        pagesMap,
        pageComponents: pageComponentsRef.current,
        getPageId,
        getPageKey,
        goToPage,
        goToNextPage,
        goToPrevPage,
      }}
    >
      {children}
    </PageConfigContext.Provider>
  );
}

PageConfigProvider.whyDidYouRender = true;
